import { useCallback, useEffect } from 'react';
import { Button, IconCode2, Wrapper } from '@screentone/core';

import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { QueryItem } from 'data/generated/graphql';
import { QueryModal } from 'features/section-page-edit/components/page-draft-section/components/query-modal/QueryModal';
import { AllessehQueryRules } from 'hooks/useAllessehContentQuery';
import { dedupeQuery } from 'utils/queryUtils';

interface EditQueryProps {
  hierarchyId: string;
  query: QueryItem;
  numItemsToShow: number;
  baseQuery: string;
  viewOnly?: boolean;
  ignoreBaseQuery?: boolean;
}

export const EditQuery = ({
  hierarchyId,
  query,
  numItemsToShow,
  baseQuery,
  viewOnly,
  ignoreBaseQuery = false
}: EditQueryProps) => {
  const { modifyEntity, applyOverfetchForModifiedQuery } = useDataModelContext();
  const { setActionModalComponent, setIsModalOpen } = useContextMenuActions();

  const allessehJsonQuery = JSON.stringify({
    ...query.attributes.query,
    query: dedupeQuery(query.attributes.query.query as AllessehQueryRules)
  });

  const handleChangeJsonQueryStr = useCallback(
    (newJsonQueryStr: string) => {
      const parsedQuery = JSON.parse(newJsonQueryStr) as Record<string, unknown>;

      modifyEntity(hierarchyId, (entity) => {
        const queryEntity = entity as QueryItem;
        queryEntity.attributes.query = parsedQuery;
      });

      applyOverfetchForModifiedQuery(query.attributes.id, parsedQuery);
    },
    [hierarchyId, modifyEntity, applyOverfetchForModifiedQuery, query.attributes.id]
  );

  const handleEditQuery = () => {
    setIsModalOpen(true);

    setActionModalComponent(
      <QueryModal
        queryId={query.attributes.id}
        queryItemHierarchyId={hierarchyId}
        allessehJsonQuery={allessehJsonQuery}
        onDismiss={() => setIsModalOpen(false)}
        onChangeJsonQueryStr={handleChangeJsonQueryStr}
        count={numItemsToShow}
        baseQuery={baseQuery}
        ignoreBaseQuery={ignoreBaseQuery}
        viewOnly={viewOnly}
      />
    );
  };

  useEffect(() => {
    setActionModalComponent(
      <QueryModal
        queryId={query.attributes.id}
        queryItemHierarchyId={hierarchyId}
        allessehJsonQuery={allessehJsonQuery}
        onDismiss={() => setIsModalOpen(false)}
        onChangeJsonQueryStr={handleChangeJsonQueryStr}
        count={numItemsToShow}
        baseQuery={baseQuery}
        ignoreBaseQuery={ignoreBaseQuery}
        viewOnly={viewOnly}
      />
    );
  }, [
    allessehJsonQuery,
    baseQuery,
    ignoreBaseQuery,
    handleChangeJsonQueryStr,
    hierarchyId,
    numItemsToShow,
    query.attributes.id,
    setActionModalComponent,
    setIsModalOpen,
    viewOnly
  ]);

  return (
    <Wrapper>
      <Button tertiary icon={IconCode2} onClick={handleEditQuery} data-testid="page-edit-query">
        {viewOnly ? 'View' : 'Edit'} query
      </Button>
    </Wrapper>
  );
};
